import type {
  AssistActionCondition,
  AssistActionConditionWebURL,
  AssistActionInstruction,
  PresetAssistAction,
} from "@mirage/shared/compose/assist-actions";

export const CONDITION_ANY_URL: AssistActionConditionWebURL = {
  type: "web_url",
  url_pattern: "https://*",
};

export type PresetActionKey =
  | "summarize-highlights"
  | "next-steps"
  | "quick-draft"
  | "sentiment-analysis"
  | "ask-me-anything"
  | "translate-summarize"
  | "critical-review"
  | "explain-code"
  | "extract-data"
  | "quotes"
  | "argument-map";

interface AssistActionPresetDefinition {
  key: PresetActionKey; // unique key used as id + for analytics
  title: string;
  conditions: AssistActionCondition[]; // matches if any of the conditions are met
  instruction: AssistActionInstruction;
  icon: string;
}

export const PRESET_ACTION_DEFINITIONS: AssistActionPresetDefinition[] = [
  // GENERAL PRESETS:
  {
    key: "summarize-highlights",
    title: "Summarize & Highlights",
    conditions: [CONDITION_ANY_URL],
    icon: "custom-icon-summarize",
    instruction: {
      type: "single_message",
      message:
        "Generate a concise summary of the following content and present the key points or major takeaways as bullet points. Display all of this directly in the current view; do not open or create a separate draft.",
    },
  },
  {
    key: "next-steps",
    title: "Next Steps",
    conditions: [CONDITION_ANY_URL],
    icon: "custom-icon-rocket",
    instruction: {
      type: "single_message",
      message:
        "Identify all tasks or to-dos in the text, along with any roles or deadlines mentioned.",
    },
  },
  {
    key: "quick-draft",
    title: "Quick Draft",
    conditions: [CONDITION_ANY_URL],
    icon: "custom-icon-paper-doc",
    instruction: {
      type: "single_message",
      message:
        "Summarize the following content into a concise format suitable for an internal email announcement (subject + body) and a short message (e.g., 280 characters) for chat or social.",
    },
  },

  // DOMAIN SPECIFIC PRESETS:
  {
    key: "sentiment-analysis",
    title: "Sentiment Analysis",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.slack.com",
        description: "Slack",
      },
      {
        type: "web_url",
        url_pattern: "https://teams.microsoft.com",
        description: "Microsoft Teams",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gong.io/call",
        description: "Gong",
      },
      {
        type: "web_url",
        url_pattern: "https://*.chorus.ai",
        description: "Chorus",
      },
      {
        type: "web_url",
        url_pattern: "https://*.my.salesforce.com",
        description: "Salesforce",
      },
      {
        type: "web_url",
        url_pattern: "https://*.glassdoor.com",
        description: "Glassdoor",
      },
      {
        type: "web_url",
        url_pattern: "https://*.indeed.com",
        description: "Indeed",
      },
      { type: "web_url", url_pattern: "https://*.g2.com", description: "G2" },
      {
        type: "web_url",
        url_pattern: "https://*.capterra.com",
        description: "Capterra",
      },
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
      {
        type: "web_url",
        url_pattern: "https://*.intercom.com",
        description: "Intercom",
      },
      {
        type: "web_url",
        url_pattern: "https://*.usertesting.com",
        description: "UserTesting",
      },
    ],
    icon: "custom-icon-heart",
    instruction: {
      type: "single_message",
      message:
        "Analyze the overall sentiment of the following text (positive, negative, neutral). Provide evidence or key quotes supporting your analysis and highlight any strongly charged opinions.",
    },
  },
  {
    key: "ask-me-anything",
    title: "Ask Me Anything",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.slack.com",
        description: "Slack",
      },
      {
        type: "web_url",
        url_pattern: "https://teams.microsoft.com",
        description: "Microsoft Teams",
      },
      {
        type: "web_url",
        url_pattern: "https://*.docs.google.com",
        description: "Google Docs",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gong.io",
        description: "Gong",
      },
      {
        type: "web_url",
        url_pattern: "https://*.my.salesforce.com",
        description: "Salesforce",
      },
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
      {
        type: "web_url",
        url_pattern: "https://*.intercom.com",
        description: "Intercom",
      },
    ],
    icon: "custom-icon-help",
    instruction: {
      type: "single_message",
      message:
        "From the text below, create a set of relevant questions and their concise answers. Focus on the kind of questions a reader might have after reading this content.",
    },
  },
  {
    key: "translate-summarize",
    title: "Translate & Summarize",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.gong.io",
        description: "Gong",
      },
      {
        type: "web_url",
        url_pattern: "https://mail.google.com",
        description: "Gmail",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gmail.com",
        description: "Gmail",
      },
      {
        type: "web_url",
        url_pattern: "https://outlook.office.com",
        description: "Outlook",
      },
      {
        type: "web_url",
        url_pattern: "https://*.outlook.com",
        description: "Outlook",
      },
      {
        type: "web_url",
        url_pattern: "https://*.slack.com",
        description: "Slack",
      },
      {
        type: "web_url",
        url_pattern: "https://*.teams.microsoft.com",
        description: "Microsoft Teams",
      },
      {
        type: "web_url",
        url_pattern: "https://*.hubspot.com",
        description: "HubSpot",
      },
      {
        type: "web_url",
        url_pattern: "https://*.marketo.com",
        description: "Marketo",
      },
      {
        type: "web_url",
        url_pattern: "https://docs.google.com",
        description: "Google Docs",
      },
      { type: "web_url", url_pattern: "https://*.g2.com", description: "G2" },
      {
        type: "web_url",
        url_pattern: "https://*.capterra.com",
        description: "Capterra",
      },
      {
        type: "web_url",
        url_pattern: "https://*.intercom.com",
        description: "Intercom",
      },
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
    ],
    icon: "custom-icon-conversation",
    instruction: {
      type: "single_message",
      message:
        "Translate the following text into English and provide a short summary of the main points.",
    },
  },
  {
    key: "critical-review",
    title: "Critical Review",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.hubspot.com",
        description: "HubSpot",
      },
      {
        type: "web_url",
        url_pattern: "https://docs.google.com",
        description: "Google Docs",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
      {
        type: "web_url",
        url_pattern: "https://*.glassdoor.com",
        description: "Glassdoor",
      },
      {
        type: "web_url",
        url_pattern: "https://*.indeed.com",
        description: "Indeed",
      },
    ],
    icon: "custom-icon-legal",
    instruction: {
      type: "single_message",
      message:
        "Summarize the main arguments, list pros and cons, and provide a brief contrarian viewpoint or weaknesses in the text.",
    },
  },
  {
    key: "explain-code",
    title: "Explain Code",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://github.com",
        description: "GitHub",
      },
      {
        type: "web_url",
        url_pattern: "https://github.*.com",
        description: "GitHub",
      },
      {
        type: "web_url",
        url_pattern: "https://stackoverflow.com",
        description: "Stack Overflow",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
    ],
    icon: "custom-icon-code",
    instruction: {
      type: "single_message",
      message:
        "Read the following code and provide an explanation of how it works, what it does, and any potential pitfalls. Include simpler pseudocode if relevant.",
    },
  },
  {
    key: "extract-data",
    title: "Extract Data",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.bamboohr.com",
        description: "BambooHR",
      },
      {
        type: "web_url",
        url_pattern: "https://*.workday.com",
        description: "Workday",
      },
      {
        type: "web_url",
        url_pattern: "https://*.amplitude.com",
        description: "Amplitude",
      },
      {
        type: "web_url",
        url_pattern: "https://*.mixpanel.com",
        description: "Mixpanel",
      },
      {
        type: "web_url",
        url_pattern: "https://*.my.salesforce.com",
        description: "Salesforce",
      },
      {
        type: "web_url",
        url_pattern: "https://*.hubspot.com",
        description: "HubSpot",
      },
      {
        type: "web_url",
        url_pattern: "https://*.analytics.google.com",
        description: "Google Analytics",
      },
      {
        type: "web_url",
        url_pattern: "https://*.xero.com",
        description: "Xero",
      },
      {
        type: "web_url",
        url_pattern: "https://*.intuit.com",
        description: "Intuit",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
      // disabling for now as we don't do well ingesting Google sheets data
      // { type: 'web_url', url_pattern: 'https://*.sheets.google.com' },
      {
        type: "web_url",
        url_pattern: "https://*.docs.google.com",
        description: "Google Docs",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
    ],
    icon: "custom-icon-analytics",
    instruction: {
      type: "single_message",
      message:
        "Scan the text for numeric data, key statistics, or tabular information. Summarize them in a structured list or simple table.",
    },
  },
  {
    key: "quotes",
    title: "Quotes",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
      {
        type: "web_url",
        url_pattern: "https://*.intercom.com",
        description: "Intercom",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gong.io",
        description: "Gong",
      },
      {
        type: "web_url",
        url_pattern: "https://*.chorus.ai",
        description: "Chorus",
      },
      {
        type: "web_url",
        url_pattern: "https://*.hubspot.com",
        description: "HubSpot",
      },
      {
        type: "web_url",
        url_pattern: "https://*.glassdoor.com",
        description: "Glassdoor",
      },
      {
        type: "web_url",
        url_pattern: "https://*.indeed.com",
        description: "Indeed",
      },
      {
        type: "web_url",
        url_pattern: "https://*.usertesting.com",
        description: "UserTesting",
      },
      {
        type: "web_url",
        url_pattern: "https://*.slack.com",
        description: "Slack",
      },
      {
        type: "web_url",
        url_pattern: "https://teams.microsoft.com",
        description: "Microsoft Teams",
      },
      {
        type: "web_url",
        url_pattern: "https://mail.google.com",
        description: "Gmail",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gmail.com",
        description: "Gmail",
      },
      {
        type: "web_url",
        url_pattern: "https://*.outlook.com",
        description: "Outlook",
      },
      {
        type: "web_url",
        url_pattern: "https://docs.google.com",
        description: "Google Docs",
      },
    ],
    icon: "custom-icon-block-quote",
    instruction: {
      type: "single_message",
      message:
        "Identify the most impactful or relevant quotes in the text. List them separately, and include a brief context for each.",
    },
  },
  {
    key: "argument-map",
    title: "Argument Map",
    conditions: [
      {
        type: "web_url",
        url_pattern: "https://*.notion.site",
        description: "Notion",
      },
      {
        type: "web_url",
        url_pattern: "https://*.notion.so",
        description: "Notion",
      },
      {
        type: "web_url",
        url_pattern: "https://*.atlassian.net",
        description: "Atlassian",
      },
      {
        type: "web_url",
        url_pattern: "https://*.gong.io",
        description: "Gong",
      },
      {
        type: "web_url",
        url_pattern: "https://*.chorus.ai",
        description: "Chorus",
      },
      {
        type: "web_url",
        url_pattern: "https://*.linkedin.com",
        description: "LinkedIn",
      },
      {
        type: "web_url",
        url_pattern: "https://docs.google.com",
        description: "Google Docs",
      },
      {
        type: "web_url",
        url_pattern: "https://github.com",
        description: "GitHub",
      },
      {
        type: "web_url",
        url_pattern: "https://github.*.com",
        description: "GitHub",
      },
      {
        type: "web_url",
        url_pattern: "https://*.zendesk.com",
        description: "Zendesk",
      },
    ],
    icon: "custom-icon-link",
    instruction: {
      type: "single_message",
      message:
        "Break down the arguments in the text into premises, supporting evidence, and conclusions. Show the logical flow of ideas.",
    },
  },
];

export const PRESET_ACTIONS: PresetAssistAction[] =
  PRESET_ACTION_DEFINITIONS.map((action) => ({
    type: "preset",
    key: action.key,
    id: `preset-action-${action.key}`,
    condition: {
      type: "any",
      conditions: action.conditions,
    },
    title: action.title,
    instruction: action.instruction,
    icon: action.icon,
  }));
